import * as amplitude from '@amplitude/analytics-browser';
import type { EnrichmentPlugin, Event } from '@amplitude/analytics-types';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { trackFacebookEvent } from './facebook-pixel';

let sessionReplayInstance: any = null;

// Map Amplitude event names to Facebook event names
// If not specified, the Amplitude event name is used as Facebook event name
export const facebookEventMap: Record<string, string> = {
  'Start anonymous analysis': 'StartAnonymousAnalysis',
  'Start analysis': 'StartAnalysis2',
  'Send message to chat': 'chat_message_sent',
};

const apiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
let initialised = false;

const siteEnrichmentPlugin = (): EnrichmentPlugin => {
  return {
    name: 'site-enrichment',
    type: 'enrichment' as const,
    setup: async () => undefined,
    execute: async (event: Event) => {
      event.event_properties = {
        ...event.event_properties,
        site: 'dashboard',
      };
      return event;
    },
  };
};

/**
 * Starts Amplitude (events, autocapture, session replay). Called only once the visitor allows
 * analytics cookies; until then trackEvent drops events, so nothing is stored or sent.
 */
export function initAmplitude(): void {
  if (initialised) return;
  initialised = true;
  if (!apiKey) {
    console.warn('Amplitude API key not found. Analytics will be disabled.');
    return;
  }

  amplitude.init(apiKey, {
    autocapture: {
      pageViews: true,
      sessions: true,
      formInteractions: true,
      fileDownloads: true,
      elementInteractions: true,
      attribution: true,
    },
    // logLevel: import.meta.env.VITE_DEBUG === 'true' ? 'debug' : 'warn',
  });

  amplitude.add(siteEnrichmentPlugin());

  sessionReplayInstance = sessionReplayPlugin({
    sampleRate: 1,
    forceSessionTracking: true,
    // Every input is masked in recordings: the Cookie Policy and the banner promise "anything you type
    // hidden". The project's Session Replay privacy setting in Amplitude overrides this, so it must also
    // be Medium or Conservative there (release checklist).
    privacyConfig: { defaultMaskLevel: 'medium' },
  });
  amplitude.add(sessionReplayInstance);

  setupExternalTransitionListeners();
}

// What a person typed, their email, the wallets and holdings they analyse, and payment tokens:
// analytics needs the event, not these, and none of it may reach an ad network.
const PERSONAL_EVENT_FIELDS = new Set(['email', 'message', 'address', 'portfolio', 'orderToken', 'orderPublicId']);

// Track custom events
export const trackEvent = (eventName: string, eventProperties?: Record<string, any>) => {
  // Before analytics consent nothing is tracked: not even queued to replay once consent arrives.
  if (!initialised) return;
  const analyticsProperties = eventProperties
    ? Object.fromEntries(Object.entries(eventProperties).filter(([field]) => !PERSONAL_EVENT_FIELDS.has(field)))
    : undefined;
  amplitude.track(eventName, analyticsProperties);

  // Map to Facebook event name using the mapping table. Meta gets the event name only.
  const facebookEventName = facebookEventMap[eventName] || eventName;
  trackFacebookEvent(facebookEventName);
};

// // Set user ID
// export const setUser = (userId: string, userProperties?: Record<string, any>) => {
//   setUserId(userId);

//   if (userProperties) {
//     const identifyObj = new Identify();
//     Object.entries(userProperties).forEach(([key, value]) => {
//       identifyObj.set(key, value);
//     });
//     identify(identifyObj);
//   }
// };

// // Track page views
// export const trackPageView = (pageName: string, pageProperties?: Record<string, any>) => {
//   track('Page Viewed', {
//     page_name: pageName,
//     ...pageProperties,
//   });
// };

// // Track user actions
// export const trackUserAction = (action: string, properties?: Record<string, any>) => {
//   track('User Action', {
//     action,
//     ...properties,
//   });
// };

// // Track trading events
// export const trackTradingEvent = (eventType: string, properties?: Record<string, any>) => {
//   track('Trading Event', {
//     event_type: eventType,
//     ...properties,
//   });
// };

// // Track portfolio analysis events
// export const trackPortfolioAnalysis = (action: string, properties?: Record<string, any>) => {
//   track('Portfolio Analysis', {
//     action,
//     ...properties,
//   });
// };

// // Track authentication events
// export const trackAuthEvent = (eventType: string, properties?: Record<string, any>) => {
//   track('Authentication', {
//     event_type: eventType,
//     ...properties,
//   });
// };

export const flushAmplitude = async (): Promise<void> => {
  try {
    await amplitude.flush();
    console.log('✅ Amplitude events flushed');

    // Also flush session replay
    if (sessionReplayInstance && typeof sessionReplayInstance.flush === 'function') {
      await sessionReplayInstance.flush();
      console.log('✅ Amplitude Session Replay flushed');
    }
  } catch (error) {
    console.error('❌ Amplitude flush error:', error);
  }
};

// Setup global listeners for external transitions
function setupExternalTransitionListeners() {
  // Flush on page unload
  window.addEventListener('beforeunload', () => {
    flushAmplitude();
  });

  // Flush on page hide (mobile browsers)
  window.addEventListener('pagehide', () => {
    flushAmplitude();
  });

  // Track clicks on external links
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');

      if (link && link.href) {
        const rawHref = link.getAttribute('href') || '';

        // Skip non-http(s) schemes like mailto:, tel:, etc.
        const schemeEndIndex = rawHref.indexOf(':');
        if (schemeEndIndex > 0) {
          const scheme = rawHref.slice(0, schemeEndIndex).toLowerCase();
          const skipSchemes = new Set(['mailto', 'tel', 'sms', 'geo', 'skype', 'whatsapp']);
          if (skipSchemes.has(scheme)) {
            return;
          }
        }

        try {
          const url = new URL(link.href, window.location.href);
          const isHttp = url.protocol === 'http:' || url.protocol === 'https:';
          const isExternal = isHttp && url.hostname !== window.location.hostname;

          if (isExternal) {
            flushAmplitude();
          }
        } catch {
          // If URL parsing fails, do nothing and allow default navigation
        }
      }
    },
    true,
  );
}
